import { useEffect, useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ComingSoonPlaceholder } from "@/components/video-library/ComingSoonPlaceholder";
import { CarouselScroller } from "@/components/programs/CarouselScroller";
import { VideoThumbnailImage } from "@/components/video-library/VideoThumbnailImage";
import { VideoPdfIndicator } from "@/components/video-library/VideoPdfIndicator";
import { readableTextOn } from "@/lib/categoryColors";

export interface HeroVideoPagerItem {
  id: string;
  title: string;
  thumbnail_url?: string | null;
  duration_minutes?: number | null;
  price?: number;
  is_free?: boolean;
  is_all_access?: boolean;
  category_name?: string | null;
  category_color?: string | null;
}


interface Props {
  videos: HeroVideoPagerItem[];
  onSelect: (id: string) => void;
  hasPdfSet?: Set<string>;
  variant?: "dark" | "light";
  mobileMode?: "fade" | "scroll";
  /** Desktop layout: paged 2-row grid (default) or single-line arrow carousel. */
  desktopMode?: "pager" | "carousel";
  /** Time spent (ms) on the slow scroll between pages. */
  scrollDurationMs?: number;
  /** Pause between page transitions (ms). */
  holdMs?: number;
}

/**
 * Responsive grid limited to 2 rows per page. Pages slide horizontally with a
 * very slow, smooth easing (~20s by default) and auto-advance.
 */
export const HeroVideoPager = ({
  videos,
  onSelect,
  hasPdfSet,
  variant = "dark",
  mobileMode = "fade",
  desktopMode = "pager",
  scrollDurationMs = 600,
  holdMs = 5000,
}: Props) => {
  const { t } = useTranslation("landing");
  // Determine columns from viewport — must match the Tailwind breakpoints below.
  const [cols, setCols] = useState<number>(() => getCols());

  useEffect(() => {
    const onResize = () => setCols(getCols());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = cols === 1;
  const perPage = cols * (isMobile ? 1 : 2); // mobile: 1 video/page, else 2 rows
  const pages = useMemo(() => {
    const out: HeroVideoPagerItem[][] = [];
    for (let i = 0; i < videos.length; i += perPage) {
      out.push(videos.slice(i, i + perPage));
    }
    return out;
  }, [videos, perPage]);

  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<number | null>(null);

  // Reset to first page if data shrinks
  useEffect(() => {
    if (page >= pages.length) setPage(0);
  }, [pages.length, page]);

  // Auto-advance scheduling
  useEffect(() => {
    if (paused || pages.length <= 1) return;
    const tick = scrollDurationMs + holdMs;
    timerRef.current = window.setTimeout(() => {
      setPage((p) => (p + 1) % pages.length);
    }, tick);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [page, paused, pages.length, scrollDurationMs, holdMs]);

  if (pages.length === 0) return null;

  return (
    <div
      className="mx-auto max-w-6xl 2xl:max-w-7xl px-[15px] md:px-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {!isMobile && desktopMode === "carousel" ? (
        <CarouselScroller className="pb-2">
          {videos.map((v) => renderCard(v, cols, onSelect, t, hasPdfSet, variant, false, true))}
        </CarouselScroller>
      ) : isMobile && mobileMode === "scroll" ? (
        <div className="-mx-4 overflow-x-auto px-4 pb-2 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-4 pl-3">
            {videos.map((v) => renderCard(v, 1, onSelect, t, hasPdfSet, variant, true))}
          </div>
        </div>
      ) : isMobile ? (
        <div className="relative">
          {pages.map((pageVideos, pageIdx) => (
            <div
              key={pageIdx}
              className={`${pageIdx === 0 ? "relative" : "absolute inset-0"} transition-opacity duration-700 ${
                pageIdx === page ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              aria-hidden={pageIdx !== page}
            >
              <div className="flex flex-wrap justify-center gap-4">
                {pageVideos.map((v) => renderCard(v, cols, onSelect, t, hasPdfSet, variant))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden">
          <div
            className="flex"
            style={{
              width: `${pages.length * 100}%`,
              transform: `translateX(-${(page * 100) / pages.length}%)`,
              transition: `transform ${scrollDurationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
              willChange: "transform",
            }}
          >
            {pages.map((pageVideos, pageIdx) => (
              <div
                key={pageIdx}
                className="shrink-0 px-1"
                style={{ width: `${100 / pages.length}%` }}
              >
                <div className="flex flex-wrap justify-center gap-4">
                  {pageVideos.map((v) => renderCard(v, cols, onSelect, t, hasPdfSet, variant))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mobileMode !== "scroll" && !(!isMobile && desktopMode === "carousel") && pages.length > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              aria-label={`Go to page ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === page ? "w-8 bg-[#F5A623]" : variant === "light" ? "w-2 bg-[#070e1e]/20 hover:bg-[#070e1e]/35" : "w-2 bg-white/30 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function getCols(): number {
  if (typeof window === "undefined") return 4;
  const w = window.innerWidth;
  if (w >= 1024) return 4; // lg
  if (w >= 640) return 2;  // sm
  return 1;
}

function renderCard(
  v: HeroVideoPagerItem,
  cols: number,
  onSelect: (id: string) => void,
  t: any,
  hasPdfSet?: Set<string>,
  variant: "dark" | "light" = "dark",
  mobileScroll = false,
  fixedWidth = false,
) {
  return (
    <button
      key={v.id}
      onClick={() => onSelect(v.id)}
      style={{
        width: mobileScroll
          ? "220px"
          : fixedWidth
            ? "276px"
            : `calc((100% - ${cols - 1}rem) / ${cols})`,
        height: mobileScroll ? "150px" : undefined,
      }}
      className={`group relative shrink-0 snap-start text-left rounded-xl overflow-hidden border transition-all hover:scale-[1.02] hover:shadow-lg aspect-video max-h-[160px] md:max-h-none bg-[#070e1e] ${
        variant === "light" ? "border-[#070e1e]/15 hover:border-[#F5A623]/70 shadow-md" : "border-white/10 hover:border-[#F5A623]/60"
      }`}
    >
      {v.thumbnail_url ? (
                        <VideoThumbnailImage
                          src={v.thumbnail_url}
                          alt={v.title}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover object-center scale-[1.08] group-hover:scale-[1.14] transition-transform duration-300"
                        />
      ) : (
        <ComingSoonPlaceholder />
      )}
      <div className="absolute inset-x-0 bottom-0 p-3 pt-10 bg-gradient-to-t from-black/85 via-black/55 to-transparent">
        <h3 className="text-sm md:text-[15px] font-semibold text-white line-clamp-2 group-hover:text-[#F5A623] transition-colors">
          {v.title}
        </h3>
        {v.duration_minutes ? (
          <div className="mt-1 flex items-center gap-1 text-xs text-white/75">
            <Clock className="h-3 w-3" />
            {v.duration_minutes} min
          </div>
        ) : null}
      </div>
      <div className="absolute top-1.5 left-1.5 flex flex-col items-start gap-1 max-w-[calc(100%-0.75rem)]">
        {v.is_all_access ? (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-[#070e1e] text-[#fcb424] border border-[#fcb424]/60 leading-none">
            {t("programs.all_access", "All-Access")}
          </span>
        ) : (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F5A623] text-[#070e1e] leading-none">
            {v.is_free ? t("programs.free", "Free").toUpperCase() : `$${v.price}`}
          </span>
        )}
        {v.category_name && v.category_color && (
          <span
            className="px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none truncate max-w-[80px]"
            style={{ backgroundColor: v.category_color, color: readableTextOn(v.category_color) }}
            title={v.category_name}
          >
            {v.category_name}
          </span>
        )}
      </div>

      {hasPdfSet?.has(v.id) && <VideoPdfIndicator count={1} variant="dark" size="md" />}
    </button>
  );
}

export default HeroVideoPager;
